import {AgentIcon, BookIcon, IdBadgeIcon, PaintbrushIcon, PlugIcon, ServerIcon} from '@primer/octicons-react'
import {advancedSearchPath, getRelativeHref, searchPath} from '@github-ui/paths'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import type {AIResourceResult} from './AIResourceResults'
import {AIResourceSearchPage, type AIResourceSearchPageState} from './AIResourceSearchPage'
import type {ResourceTypeFilterProps} from './ResourceTypeFilter'
import type {
  AIResourceSearchCategory,
  AIResourceSearchError,
  AIResourceSearchKind,
  AIResourceSearchKindCount,
  AIResourceSearchPayload,
  AIResourceSearchPayloadInput,
  AIResourceSearchResult,
  AIResourceNoResultsProps,
} from './types'

export type AIResourceSearchProps = {
  counts: readonly AIResourceSearchKindCount[]
  isLoadingCounters: boolean
  navigateToSearch: (query: Record<string, string | null>) => void
  payload: AIResourceSearchPayloadInput | null
  query: string
  renderError: (error: AIResourceSearchError, index: number) => React.ReactNode
  renderNoResults: (props: AIResourceNoResultsProps) => React.ReactNode
  resultKind: AIResourceSearchKind
}

const resourceTypes = [
  {label: 'Skill', mediaType: 'application/ai-skill', qualifier: 'type:"Skill"', icon: BookIcon},
  {
    label: 'MCP server',
    filterLabel: 'MCP Server',
    mediaType: 'application/mcp-server+json',
    mediaTypeAlias: 'application/mcp-server-card+json',
    qualifier: 'type:"MCP Server"',
    icon: ServerIcon,
  },
  {label: 'Agent', mediaType: 'application/agent+json', qualifier: 'type:"Agent"', icon: AgentIcon},
  {
    label: 'Plugin',
    mediaType: 'application/vnd.github.copilot-plugin',
    qualifier: 'type:"Plugin"',
    icon: PlugIcon,
  },
  {
    label: 'A2A agent',
    mediaType: 'application/a2a-agent-card+json',
    qualifier: 'type:"A2A agent"',
    icon: IdBadgeIcon,
  },
  {label: 'Canvas', mediaType: 'application/canvas+json', qualifier: 'type:"Canvas"', icon: PaintbrushIcon},
] as const

const clearedSearchParameters = {p: null, page_token: null, l: null, s: null, o: null}
const rawBase64UrlPattern = /^[A-Za-z0-9_-]+$/
const mcpServerNameMaxLength = 200
const mcpServerNamePattern = /^[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\/[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?$/
const mcpServerMediaTypes: ReadonlySet<string> = new Set([
  'application/mcp-server+json',
  'application/mcp-server-card+json',
])

type AgentFinderPageToken = {
  version: 2
  totalCount: number
  pageSize: number
  currentPage: number
  pageCount: number
  inputHash: string
  signature: string
  targetPage?: number
  [key: string]: unknown
}

type AIResourcePagination = {
  token: AgentFinderPageToken
  currentPage: number
  pageCount: number
  totalCount: number
}

export function AIResourceSearch({
  counts,
  isLoadingCounters,
  navigateToSearch,
  payload,
  query,
  renderError,
  renderNoResults,
  resultKind,
}: AIResourceSearchProps) {
  const pagination = useMemo(() => getPagination(payload), [payload])
  const getPageHref = useCallback(
    (page: number) => {
      if (!pagination) return '#'

      return getRelativeHref(searchPath, undefined, {
        p: null,
        page_token: encodePageToken(pagination.token, page),
      })
    },
    [pagination],
  )
  const onPageChange = useCallback(
    (event: React.MouseEvent, page: number) => {
      if (!pagination || event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- preserve native link-modifier behavior
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      event.preventDefault()
      if (page === pagination.currentPage) return

      window.scrollTo(0, 0)
      navigateToSearch({p: null, page_token: encodePageToken(pagination.token, page)})
    },
    [navigateToSearch, pagination],
  )

  const categories: AIResourceSearchCategory[] = counts.map(count => ({
    label: capitalize(count.readableNamePlural),
    href: getRelativeHref(searchPath, undefined, {...clearedSearchParameters, q: query, type: count.name}),
    icon: count.icon,
    count: count.final && !count.error ? count.total : undefined,
    current: count.name === 'ai_resources',
  }))

  const filters: ResourceTypeFilterProps = {
    resourceTypes: resourceTypes.map(resourceType => ({
      label: 'filterLabel' in resourceType ? resourceType.filterLabel : resourceType.label,
      href: getFilterHref(query, resourceType.qualifier),
      icon: resourceType.icon,
    })),
    labelFilterHref: getFilterHref(query, 'label:'),
    advancedSearchHref: getRelativeHref(advancedSearchPath, undefined, {q: query}),
  }

  return (
    <AIResourceSearchPage
      categories={categories}
      elapsedMilliseconds={payload?.elapsed_millis}
      filters={filters}
      isLoadingCounters={isLoadingCounters}
      onPageChange={onPageChange}
      query={query}
      renderError={renderError}
      renderNoResults={renderNoResults}
      resultKind={resultKind}
      searchKinds={[...counts]}
      state={getState(payload, pagination, getPageHref)}
    />
  )
}

function getFilterHref(query: string, qualifier: string) {
  return getRelativeHref(searchPath, undefined, {
    ...clearedSearchParameters,
    q: `${query} ${qualifier}`.trim(),
    type: 'ai_resources',
  })
}

function getState(
  payload: AIResourceSearchPayloadInput | null,
  pagination: AIResourcePagination | null,
  getPageHref: (page: number) => string,
): AIResourceSearchPageState {
  if (!payload) return {status: 'loading'}
  if (payload.errors.length > 0) return {status: 'error', errors: payload.errors}
  if (!isAIResourceSearchPayload(payload)) return {status: 'empty'}

  const results = payload.results.flatMap(result => {
    const mappedResult = mapResult(result)
    return mappedResult ? [mappedResult] : []
  })
  if (results.length === 0 && (!pagination || pagination.totalCount === 0)) return {status: 'empty'}

  return {
    status: 'success',
    results,
    totalCount: pagination?.totalCount,
    currentPage: pagination?.currentPage,
    pageCount: pagination?.pageCount,
    getPageHref: pagination ? getPageHref : undefined,
  }
}

function getPagination(payload: AIResourceSearchPayloadInput | null): AIResourcePagination | null {
  if (!payload || !isAIResourceSearchPayload(payload) || !payload.page_token) return null

  const token = decodePageToken(payload.page_token)
  if (
    !token ||
    token.totalCount !== payload.result_count ||
    token.currentPage !== payload.page ||
    token.pageCount !== payload.page_count
  ) {
    return null
  }

  return {
    token,
    totalCount: token.totalCount,
    currentPage: token.currentPage,
    pageCount: token.pageCount,
  }
}

function isAIResourceSearchPayload(payload: AIResourceSearchPayloadInput): payload is AIResourceSearchPayload {
  return payload.type === 'ai_resources' && 'results' in payload && Array.isArray(payload.results)
}

function decodePageToken(value: string): AgentFinderPageToken | null {
  if (!rawBase64UrlPattern.test(value)) return null

  try {
    const padding = '='.repeat((4 - (value.length % 4)) % 4)
    const binary = globalThis.atob(value.replaceAll('-', '+').replaceAll('_', '/') + padding)
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))
    const token: unknown = JSON.parse(new TextDecoder().decode(bytes))
    if (!isAgentFinderPageToken(token)) return null

    return token
  } catch {
    return null
  }
}

function isAgentFinderPageToken(value: unknown): value is AgentFinderPageToken {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const token = value as Record<string, unknown>
  return (
    token.version === 2 &&
    isNonNegativeInteger(token.totalCount) &&
    isPositiveInteger(token.pageSize) &&
    isPositiveInteger(token.currentPage) &&
    isNonNegativeInteger(token.pageCount) &&
    typeof token.inputHash === 'string' &&
    token.inputHash.length > 0 &&
    typeof token.signature === 'string' &&
    token.signature.length > 0 &&
    (token.targetPage === undefined || isPositiveInteger(token.targetPage)) &&
    (token.pageCount === 0 ? token.currentPage === 1 : token.currentPage <= token.pageCount)
  )
}

function encodePageToken(token: AgentFinderPageToken, targetPage: number) {
  const bytes = new TextEncoder().encode(JSON.stringify({...token, targetPage}))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)

  return globalThis.btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0
}

function mapResult(result: AIResourceSearchResult): AIResourceResult | null {
  const href = result.href
  if (!isSafeUrl(href)) return null

  const publisher = result.publisher
  const resourceType = resourceTypes.find(
    candidate =>
      candidate.mediaType === result.resource_type ||
      ('mediaTypeAlias' in candidate && candidate.mediaTypeAlias === result.resource_type),
  )
  return {
    id: result.id,
    href,
    name: result.name,
    description: result.description ?? undefined,
    resourceType: resourceType?.label ?? result.resource_type,
    resourceTypeIcon: resourceType?.icon,
    labels: result.labels,
    publisher: publisher && isSafeUrl(publisher.href) ? publisher : undefined,
    installTarget: getInstallTarget(result),
  }
}

function getInstallTarget(result: AIResourceSearchResult) {
  const install = result.install
  if (
    !mcpServerMediaTypes.has(result.resource_type) ||
    install?.type !== 'mcp_server' ||
    typeof install.server_name !== 'string' ||
    typeof install.url !== 'string' ||
    install.server_name.length > mcpServerNameMaxLength ||
    !mcpServerNamePattern.test(install.server_name) ||
    !isMcpRegistryUrl(install.url)
  ) {
    return undefined
  }

  const serverName = install.server_name
  return {
    name: serverName,
    full_name: serverName,
    api_name: serverName,
  }
}

function isMcpRegistryUrl(value: string) {
  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname === 'api.mcp.github.com'
  } catch {
    return false
  }
}

function isSafeUrl(value: string | null): value is string {
  if (!value || !URL.canParse(value)) return false

  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const {protocol} = new URL(value)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function capitalize(value: string) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}
